// Vortex Auth (Better Auth) runtime for the plasma restaurant portal.
//
// Mirrors crm's `apps/web/src/lib/auth-runtime.better-auth.tsx`: it configures a
// `createVortexBetterAuthRuntime` instance, then exposes the provider stack
// (`AuthRuntimeProvider` -> `BetterAuthConvexProvider` -> `ConvexProviderWithAuth`)
// that feeds the vortex-auth bearer token into the single `ConvexReactClient`.
//
// Deviation from crm: plasma provisions user records server-side via the Better
// Auth Convex component (`apps/backend/convex/betterAuth.ts` + webhook routes),
// NOT via a client-side identity provisioner. crm passes
// `runtime.BetterAuthConvexIdentityProvisioner` (getCurrentUser +
// provisionCurrentUser); plasma has no public `provisionCurrentUser` mutation, so
// we omit the optional `identityProvisioner` prop. Active-org context is sourced
// from `organizations.getActive` in `_authenticated.tsx`, also matching crm.
import { createBetterAuthConvexClient } from "@vortexnyc/vortex-core/auth/better-auth/client";
import {
  createVortexAuthEventCapture,
  createVortexAuthFlowStorage,
  createVortexAuthRoutePaths,
  createVortexBetterAuthRuntime,
  type VortexBetterAuthClient,
} from "@vortexnyc/vortex-core/auth/react";
import type { ConvexReactClient } from "convex/react";
import type { ReactNode } from "react";

import { env } from "~/env";

const betterAuthBaseUrl = env.PUBLIC_VORTEX_AUTH_URL;
const authFlow = createVortexAuthFlowStorage({
  storageKeyPrefix: "plasma.portal.auth",
});

export const authRoutePaths = createVortexAuthRoutePaths();

// Pre-wired Better Auth + Convex client. Mirrors `integrations/vortex-auth/client.ts`
// but is recreated here so the runtime owns its own instance, exactly like crm.
const betterAuthClient: VortexBetterAuthClient = createBetterAuthConvexClient({
  baseURL: betterAuthBaseUrl,
  fetchOptions: {
    credentials: "include",
  },
});

export const runtime = createVortexBetterAuthRuntime({
  authClient: betterAuthClient,
  betterAuthBaseUrl,
  signInPath: authRoutePaths.signInPath,
  signUpPath: authRoutePaths.signUpPath,
  copy: {
    signInTitle: "Sign in",
    signInDescription:
      "Access the plasma portal with the shared workspace authentication flow.",
    signInUnavailableTitle: "Sign-in is unavailable",
    signInUnavailableDescription:
      "The portal couldn't load the Better Auth runtime for this page.",
    signUpTitle: "Create account",
    signUpDescription:
      "Create your account, then continue into portal organization setup.",
    signUpUnavailableTitle: "Sign-up is unavailable",
    signUpUnavailableDescription:
      "The portal couldn't load the Better Auth runtime for this page.",
  },
});

// Mounts the vortex-auth runtime as the SOLE auth source for the supplied
// ConvexReactClient. No client-side identity provisioner (server-side webhook
// handles provisioning).
export function AuthRuntimeProvider(args: {
  children: ReactNode;
  convex: ConvexReactClient;
}) {
  return (
    <runtime.AuthRuntimeProvider convex={args.convex}>
      {args.children}
    </runtime.AuthRuntimeProvider>
  );
}

export const useAppAuth = runtime.useAppAuth;
export const useAppUser = runtime.useAppUser;
export const useAppAuthActions = runtime.useAppAuthActions;
export const {
  clearPendingPostSignUpSync,
  consumePendingAuthFlow,
  markPendingAuthFlow,
  markPendingPostSignUpSync,
  toSafeRedirectPath,
} = authFlow;
// crm wires this into PostHog via its `captureEvent` helper. plasma's portal has
// no equivalent module-level capture yet, so we pass a no-op for F1. Analytics
// wiring can be added in a later stage without touching the provider tree.
const noopCaptureSink = () => {
  // Analytics capture intentionally not wired in the portal yet (F1).
  // crm routes this into PostHog; plasma can add a sink later without
  // touching the provider tree.
};
export const captureAuthEvent = createVortexAuthEventCapture(noopCaptureSink);
